import { Content } from "@/components/layouts/content";
import { bir2307Service } from "@/features/bir2307/api";
import type { Bir2307Certificate, Bir2307Status } from "@/features/bir2307/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

const phpFormat = (n: number) => n.toLocaleString("en-PH", { style: "currency", currency: "PHP" });

const statusColors: Record<Bir2307Status, string> = {
	received: "bg-amber-100 text-amber-800 border-amber-200",
	verified: "bg-blue-100 text-blue-800 border-blue-200",
	filed: "bg-green-100 text-green-800 border-green-200",
};

export const Route = createFileRoute("/_dashboard/bir-2307")({
	component: Bir2307Page,
});

function Bir2307Page() {
	const queryClient = useQueryClient();
	const [page, setPage] = useState(1);
	const [statusFilter, setStatusFilter] = useState<Bir2307Status | "">("");

	const listQuery = useQuery({
		queryKey: ["bir-2307", page, statusFilter],
		queryFn: () =>
			bir2307Service.list({
				page,
				per_page: 20,
				...(statusFilter ? { "filter[status]": statusFilter } : {}),
			}),
	});

	const agingQuery = useQuery({
		queryKey: ["bir-2307", "aging"],
		queryFn: () => bir2307Service.aging(),
	});

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: ["bir-2307"] });
	};

	const verifyMutation = useMutation({
		mutationFn: (id: string) => bir2307Service.verify(id),
		onSuccess: invalidate,
	});

	const fileMutation = useMutation({
		mutationFn: ({ id, ref }: { id: string; ref?: string }) => bir2307Service.file(id, ref),
		onSuccess: invalidate,
	});

	return (
		<Content>
			<div className="mx-auto max-w-7xl space-y-6 px-6 py-8">
				<div className="flex items-start justify-between">
					<div>
						<h1 className="text-2xl font-semibold text-gray-900">BIR 2307 Certificates</h1>
						<p className="mt-1 text-sm text-gray-600">
							Creditable withholding tax certificates issued by customers. Verify the math, then file with BIR to claim the CWT credit on our income tax return.
						</p>
					</div>
				</div>

				{agingQuery.data && (
					<div className="rounded-lg border border-gray-200 bg-white p-4">
						<div className="mb-3 flex items-center justify-between">
							<h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Aging — Unfiled Certificates</h2>
							<div className="text-xs text-gray-500">As of {agingQuery.data.meta.as_of}</div>
						</div>
						<div className="grid grid-cols-2 gap-3 md:grid-cols-5">
							{Object.entries(agingQuery.data.buckets).map(([key, bucket]) => (
								<div key={key} className="rounded border border-gray-200 bg-gray-50 p-3">
									<div className="text-xs uppercase tracking-wide text-gray-500">{bucket.range}</div>
									<div className="mt-1 text-lg font-semibold text-gray-900">{bucket.count}</div>
									<div className="text-xs tabular-nums text-gray-600">{phpFormat(bucket.total_tax)}</div>
								</div>
							))}
						</div>
						<div className="mt-3 border-t border-gray-200 pt-3 text-sm">
							<span className="text-gray-600">Total outstanding CWT credit:</span>{" "}
							<span className="font-semibold tabular-nums">{phpFormat(agingQuery.data.meta.total_tax_outstanding)}</span>{" "}
							<span className="text-gray-500">({agingQuery.data.meta.total_unfiled} unfiled certs)</span>
						</div>
					</div>
				)}

				<div className="flex items-center gap-3">
					<label className="text-xs uppercase tracking-wide text-gray-500" htmlFor="status-filter">
						Status
					</label>
					<select
						id="status-filter"
						value={statusFilter}
						onChange={(e) => {
							setStatusFilter(e.target.value as Bir2307Status | "");
							setPage(1);
						}}
						className="h-9 rounded border border-gray-300 px-3 text-sm"
					>
						<option value="">All</option>
						<option value="received">Received</option>
						<option value="verified">Verified</option>
						<option value="filed">Filed</option>
					</select>
				</div>

				<div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
					<table className="w-full text-sm">
						<thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
							<tr>
								<th className="px-3 py-2">Cert #</th>
								<th className="px-3 py-2">Payor</th>
								<th className="px-3 py-2">Period</th>
								<th className="px-3 py-2 text-right">Gross</th>
								<th className="px-3 py-2 text-right">Tax</th>
								<th className="px-3 py-2">ATC</th>
								<th className="px-3 py-2">Status</th>
								<th className="px-3 py-2 text-right">Age</th>
								<th className="w-44" />
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-100">
							{listQuery.data?.data.length === 0 && (
								<tr>
									<td colSpan={9} className="px-3 py-6 text-center text-gray-500 italic">
										No 2307 certificates yet.
									</td>
								</tr>
							)}
							{listQuery.data?.data.map((cert) => (
								<CertRow
									key={cert.id}
									cert={cert}
									onVerify={() => verifyMutation.mutate(cert.id)}
									onFile={(ref) => fileMutation.mutate({ id: cert.id, ref })}
									isPending={
										(verifyMutation.isPending && verifyMutation.variables === cert.id) || (fileMutation.isPending && fileMutation.variables?.id === cert.id)
									}
								/>
							))}
						</tbody>
					</table>
				</div>

				{listQuery.data && listQuery.data.meta.last_page > 1 && (
					<div className="flex items-center justify-between">
						<div className="text-sm text-gray-600">
							Page {listQuery.data.meta.current_page} of {listQuery.data.meta.last_page} · {listQuery.data.meta.total} certificates
						</div>
						<div className="flex gap-2">
							<button
								type="button"
								onClick={() => setPage(Math.max(1, page - 1))}
								disabled={page === 1}
								className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40"
							>
								Previous
							</button>
							<button
								type="button"
								onClick={() => setPage(Math.min(listQuery.data.meta.last_page, page + 1))}
								disabled={page === listQuery.data.meta.last_page}
								className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40"
							>
								Next
							</button>
						</div>
					</div>
				)}
			</div>
		</Content>
	);
}

function CertRow({
	cert,
	onVerify,
	onFile,
	isPending,
}: {
	cert: Bir2307Certificate;
	onVerify: () => void;
	onFile: (ref?: string) => void;
	isPending: boolean;
}) {
	return (
		<tr className={cert.has_math_drift ? "bg-amber-50" : ""}>
			<td className="px-3 py-2 font-mono text-xs">{cert.certificate_no}</td>
			<td className="px-3 py-2">
				<div className="font-medium text-gray-900">{cert.payor_name}</div>
				<div className="text-xs text-gray-500">{cert.payor_tin}</div>
			</td>
			<td className="px-3 py-2 text-xs text-gray-700">
				{cert.period_start} → {cert.period_end}
			</td>
			<td className="px-3 py-2 text-right tabular-nums">{phpFormat(cert.gross_amount)}</td>
			<td className="px-3 py-2 text-right tabular-nums">
				{phpFormat(cert.tax_amount)}
				{cert.has_math_drift && <div className="text-[10px] text-amber-700">⚠ Expected {phpFormat(cert.computed_tax)}</div>}
			</td>
			<td className="px-3 py-2 text-xs text-gray-600">{cert.atc_code ?? "—"}</td>
			<td className="px-3 py-2">
				<span className={`inline-block rounded border px-2 py-0.5 text-xs ${statusColors[cert.status]}`}>{cert.status}</span>
			</td>
			<td className="px-3 py-2 text-right text-xs text-gray-600">{cert.age_days}d</td>
			<td className="px-3 py-2 text-right">
				{cert.status === "received" && (
					<button type="button" onClick={onVerify} disabled={isPending} className="text-xs text-blue-700 hover:underline disabled:opacity-50">
						{isPending ? "..." : "Verify"}
					</button>
				)}
				{cert.status === "verified" && (
					<button
						type="button"
						onClick={() => {
							const ref = window.prompt("Filing reference (eAFS, paper receipt, etc.):") ?? undefined;
							onFile(ref);
						}}
						disabled={isPending}
						className="text-xs text-green-700 hover:underline disabled:opacity-50"
					>
						{isPending ? "..." : "Mark filed"}
					</button>
				)}
				{cert.status === "filed" && <span className="text-xs text-gray-400">{cert.filing_reference ?? "filed"}</span>}
			</td>
		</tr>
	);
}
